import { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Form } from "@/components/ui/form";
import { FormattedTextField } from "@/components/form/FormattedTextField";
import { PipelineFormProvider, PipelineFormSection } from "@/components/form/PipelineFormContext";
import { useAuth } from "@/features/auth";
import { ClientesService } from "@/features/clientes/services/clientes.service";
import type { Cliente } from "@/features/clientes/types/cliente";
import { clienteBasicoSchema, type ClienteBasicoValues } from "./ClienteFormSchema";

interface ClienteBasicoModalProps {
  open: boolean;
  /** Presente = edição; ausente = criação */
  cliente?: Cliente;
  onClose: () => void;
  onSuccess: (cliente: Cliente) => void;
}

function toFormValues(cliente?: Cliente): ClienteBasicoValues {
  return {
    nome: cliente?.nome || "",
    tel1: cliente?.tel1 || "",
    tel2: cliente?.tel2 || "",
    cpf: cliente?.cpf || "",
    email: cliente?.email || "",
  };
}

/** Criação/edição só dos dados de identificação do cliente (fluxo de lead) — salva direto no banco */
export function ClienteBasicoModal({ open, cliente, onClose, onSuccess }: ClienteBasicoModalProps) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const isEdit = !!cliente?.id;

  const form = useForm<ClienteBasicoValues>({
    resolver: zodResolver(clienteBasicoSchema),
    defaultValues: toFormValues(cliente),
    mode: "onChange",
  });

  useEffect(() => {
    if (open) form.reset(toFormValues(cliente));
  }, [open, cliente, form]);

  const handleSave = form.handleSubmit(async (data) => {
    setIsSaving(true);
    try {
      let saved: Cliente;
      if (isEdit) {
        // null limpa o campo opcional apagado; tenant_id é obrigatório no update do Supabase
        saved = await ClientesService.atualizarCliente(String(cliente.id), {
          tenant_id: cliente.tenant_id,
          nome: data.nome.trim(),
          tel1: data.tel1,
          tel2: data.tel2 || null,
          cpf: data.cpf || null,
          email: data.email || null,
        } as Partial<Cliente>);
      } else {
        const tenantId = Number(profile?.tenant_id);
        const createdBy = profile?.uuid;
        if (!tenantId || !createdBy) {
          toast.error("Usuário ou tenant não identificado");
          return;
        }
        saved = await ClientesService.criarCliente({
          tenant_id: tenantId,
          created_by: createdBy,
          nome: data.nome.trim(),
          tel1: data.tel1,
          tel2: data.tel2 || undefined,
          cpf: data.cpf || undefined,
          email: data.email || undefined,
          nacionalidade: "Brasileira",
          estado_civil: "solteiro",
          status: "draft",
        });
      }

      await queryClient.invalidateQueries({ queryKey: ["clientes"] });
      toast.success(isEdit ? "Cliente atualizado" : "Cliente criado");
      onSuccess(saved);
    } catch (error: unknown) {
      console.error("Erro ao salvar cliente:", error);
      toast.error(error instanceof Error ? error.message : "Erro ao salvar cliente");
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      maskClosable={false}
      destroyOnHidden
      centered
      width="min(520px, 96vw)"
      title={isEdit ? "Editar cliente" : "Novo cliente"}
      className="pipeline-submodal"
      footer={[
        <Button key="cancel" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>,
        <Button key="save" type="primary" loading={isSaving} onClick={() => void handleSave()}>
          {isEdit ? "Salvar alterações" : "Criar cliente"}
        </Button>,
      ]}
    >
      <PipelineFormProvider variant="pipeline">
        <Form {...form}>
          <form
            autoComplete="off"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              void handleSave();
            }}
            className="pt-2"
          >
            <PipelineFormSection>
              <div className="flex flex-col">
                <FormattedTextField form={form} name="nome" label="Nome" formatter="texto" placeholder="Nome completo" required />
                <FormattedTextField form={form} name="tel1" label="Telefone" formatter="telefone" placeholder="(00) 00000-0000" required />
                <FormattedTextField form={form} name="tel2" label="Telefone 2" formatter="telefone" placeholder="(00) 00000-0000" />
                <FormattedTextField form={form} name="cpf" label="CPF/CNPJ" formatter="cpfCnpj" placeholder="000.000.000-00" />
                <FormattedTextField form={form} name="email" label="Email" formatter="texto" placeholder="exemplo@email.com" />
              </div>
            </PipelineFormSection>
          </form>
        </Form>
      </PipelineFormProvider>
    </Modal>
  );
}
